import { HistoryOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Typography,
} from 'antd';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CardTypeResponse } from '../../model/Card-type.model';
import FeeScheduleResponse from '../../model/Fee-schedule.model';
import { BankResponse } from '../../model/Bank.model';
import CardTypeService from '../../service/card-type-service/Card-type.service';
import FeeScheduleService from '../../service/fee-schedule-service/Fee-schedule.service';
import BankService from '../../service/bank-service/Bank.service';
import FeeScheduleHistory from './FeeScheduleHistory';

interface EditFeeScheduleModalProps {
  feeSchedule: FeeScheduleResponse | null;
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const EditFeeScheduleModal: React.FC<EditFeeScheduleModalProps> = ({
  feeSchedule,
  visible,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [cardTypes, setCardTypes] = useState<CardTypeResponse[]>([]);
  const [banks, setBanks] = useState<BankResponse[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    console.log('🔄 Form initialization useEffect triggered:', {
      visible,
      feeSchedule,
    }); // Debug log
    if (visible && feeSchedule) {
      console.log('📝 Setting form values:', {
        cardTypeId: feeSchedule.cardType.cardTypeId,
        bankId: feeSchedule.bank?.id || null,
        feeRate: feeSchedule.feeRate,
        effectiveDate: moment(feeSchedule.effectiveDate),
      }); // Debug log

      form.setFieldsValue({
        cardTypeId: feeSchedule.cardType.cardTypeId,
        bankId: feeSchedule.bank?.id || null,
        feeRate: feeSchedule.feeRate,
        effectiveDate: moment(feeSchedule.effectiveDate),
      });

      console.log('✅ Form values set successfully'); // Debug log
    }
  }, [visible, feeSchedule, form]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cardTypesData, banksData] = await Promise.all([
          CardTypeService.getCardTypeList(),
          BankService.getBankList(),
        ]);
        setCardTypes(cardTypesData);
        setBanks(banksData);
      } catch (error: any) {
        console.error('Error fetching data:', error);
        toast.error(error.message || 'Có lỗi xảy ra khi tải dữ liệu');
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async () => {
    console.log('🚀 handleSubmit called'); // Debug log
    try {
      console.log('📝 Validating form fields...'); // Debug log
      const values = await form.validateFields();
      console.log('✅ Form validation passed:', values); // Debug log

      setLoading(true);

      console.log('Form values:', values); // Log giá trị form
      console.log('🔍 FeeSchedule structure:', feeSchedule); // Debug log để xem cấu trúc
      console.log('🔍 PosDevice:', feeSchedule?.posDevice); // Debug log để xem posDevice

      // Tạo đối tượng request với đầy đủ thông tin theo đúng FeeScheduleRequestDto
      const updateData = {
        cardTypeId: feeSchedule?.cardType.cardTypeId || null,
        posDeviceId: (feeSchedule?.posDevice as any)?.id || null, // Type assertion để tránh lỗi TypeScript
        bankId: feeSchedule?.bank?.id || null,
        feeRate: values.feeRate,
        effectiveDate: values.effectiveDate.format('YYYY-MM-DD'), // Sửa format ngày
        description: feeSchedule?.description || undefined,
      };

      console.log('Sending data to API:', updateData); // Log dữ liệu gửi đi
      console.log('FeeSchedule ID:', feeSchedule?.id); // Debug log

      // Kiểm tra xem posDeviceId có tồn tại không
      if (!updateData.posDeviceId) {
        console.log('❌ Missing posDeviceId, aborting API call'); // Debug log
        console.log(
          '🔍 Available posDevice fields:',
          Object.keys(feeSchedule?.posDevice || {})
        ); // Debug log
        toast.error('Không tìm thấy thông tin thiết bị POS, vui lòng thử lại');
        setLoading(false);
        return;
      }

      console.log('🌐 Calling API...'); // Debug log
      await FeeScheduleService.updateFeeSchedule(feeSchedule!.id, updateData);
      console.log('✅ API call successful'); // Debug log

      toast.success('Cập nhật biểu phí thành công');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.log('❌ Error in handleSubmit:', error); // Debug log
      if (error.errorFields) {
        console.log('📝 Form validation error:', error.errorFields); // Debug log
        return; // Validation error, no need to show toast
      }
      console.error('Error updating fee schedule:', error);
      toast.error(error.message || 'Có lỗi xảy ra khi cập nhật biểu phí');
    } finally {
      console.log('🏁 handleSubmit finished, setting loading to false'); // Debug log
      setLoading(false);
    }
  };

  if (!feeSchedule) return null;

  // Tìm CardType trong danh sách để hiển thị thông tin
  const cardType = cardTypes.find(
    (ct) => ct.cardTypeId === feeSchedule.cardType.cardTypeId
  );

  return (
    <Modal
      title="Chỉnh sửa biểu phí"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      maskClosable={false}
    >
      <Alert
        message="Lưu ý về chỉnh sửa biểu phí"
        description={
          <div>
            <p>Khi chỉnh sửa biểu phí, hệ thống sẽ:</p>
            <ul>
              <li>Cập nhật trực tiếp biểu phí hiện tại</li>
            </ul>
            <p>
              Bạn chỉ có thể chỉnh sửa mức phí và ngày hiệu lực, không thể thay
              đổi loại thẻ hoặc ngân hàng.
            </p>
          </div>
        }
        type="info"
        showIcon
        className="mb-4"
      />

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="Thiết bị POS">
              <Input
                value={
                  feeSchedule.posDevice
                    ? feeSchedule.posDevice.deviceCode
                    : 'Không có'
                }
                disabled
                className="bg-gray-100"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Loại thẻ">
              <Input
                value={cardType ? cardType.name : feeSchedule.cardType.name}
                disabled
                className="bg-gray-100"
              />
              <Form.Item name="cardTypeId" hidden>
                <Input />
              </Form.Item>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="Ngân hàng">
              <Input
                value={
                  feeSchedule.bank ? `${feeSchedule.bank.name} ` : 'Không có'
                }
                disabled
                className="bg-gray-100"
              />
              <Form.Item name="bankId" hidden>
                <Input />
              </Form.Item>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="feeRate"
              label={<span>Mức phí (%)</span>}
              rules={[
                { required: true, message: 'Vui lòng nhập mức phí' },
                { type: 'number', min: 0, message: 'Mức phí không được âm' },
              ]}
            >
              <InputNumber
                className="w-full"
                placeholder="Nhập mức phí"
                step={0.01}
                precision={2}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="effectiveDate"
              label={<span>Ngày hiệu lực</span>}
              rules={[
                { required: true, message: 'Vui lòng chọn ngày hiệu lực' },
              ]}
              tooltip="Biểu phí sẽ có hiệu lực từ ngày này"
            >
              <DatePicker
                className="w-full"
                format="DD/MM/YYYY"
                placeholder="Chọn ngày hiệu lực"
              />
            </Form.Item>
          </Col>
        </Row>

        <div className="flex justify-between gap-2 mt-6">
          <div>
            <Button onClick={onClose} className="mr-2">
              Hủy
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              onClick={() => console.log('🔘 Submit button clicked')} // Debug log
            >
              {loading ? 'Đang xử lý...' : 'Hoàn thành'}
            </Button>
          </div>
        </div>
      </Form>

      {showHistory && feeSchedule && (
        <>
          <Divider />
          <FeeScheduleHistory
            posDeviceId={(feeSchedule.posDevice as any)?.id || 0}
            cardTypeId={feeSchedule.cardType.cardTypeId}
            cardTypeName={feeSchedule.cardType.name}
          />
        </>
      )}
    </Modal>
  );
};

export default EditFeeScheduleModal;
